import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { useField } from 'formik';
import { StateModel } from 'redux/reducers';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useSelector } from 'react-redux';
import { AssociationEntries } from 'enums/onboarding/crm';
import { ServiceProviderFormItem } from '.';
import { IDocumentAssociationInfo } from 'typings/documents/documents';
import { ServiceProviderFromQuery } from 'api/crm/intakeFormAPI';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import {
  getPlaceholderForSelect,
  generateFieldPlaceholderForAdditionalField,
} from '../utils';

// components
import FieldIssue from '../FieldIssue';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import {
  Text,
  AutocompleteEventOption,
  Button,
  Card,
  Col,
  FieldValidationRule,
  FormField,
  SectionIntro,
  Tooltip,
} from '@ui';
import ServiceProvidersAutocomplete, {
  IProps as ServiceProvidersAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/ServiceProvidersAutocomplete';
import AdditionalField, {
  AdditionalFieldFormItemWithNullableIdModel,
} from 'components/Forms/FormComponents/AdditionalField';

interface IProps {
  index: number;
  fieldName: string;
  deleteCallback?: () => void;
  isMandatoryProvider?: boolean;
}

const ServiceProviderForm = ({
  index,
  fieldName,
  isMandatoryProvider,
  deleteCallback,
}: IProps) => {
  const { t } = useTranslation('onboarding');
  const [field, , fieldHelpers] = useField<ServiceProviderFormItem>(fieldName);
  const currentContactId = useSelector<StateModel>(
    (state) => state.auth.contactData?._id,
  );

  const documentAssociation = useMemo<
    IDocumentAssociationInfo | undefined
  >(() => {
    return {
      id: currentContactId as string,
      type: AssociationEntries.Contact,
    };
  }, [currentContactId]);

  const handleTemplateSelect = (
    newValue: any,
    option: AutocompleteEventOption<ServiceProviderFromQuery>,
  ) => {
    if (!option.model) {
      return;
    }

    const additionalFields: AdditionalFieldFormItemWithNullableIdModel[] =
      option.model.additionalFields
        .filter(
          ({ relatedTo }) => relatedTo === 'any' || relatedTo === 'contact',
        )
        .map((e) => ({
          name: e.name,
          description: e.description,
          type: e.type,
          value: undefined,
          relatedTo: e.relatedTo,
          options: {
            onboardingRequired: e.options.onboardingRequired,
            isForRegulatedOnly: e.options.isForRegulatedOnly,
            documentRequiresCertification:
              e.options.documentRequiresCertification,
            selectOptions: e.options.selectOptions.length
              ? e.options.selectOptions.map((e) => JSON.parse(e.valueJSON))
              : [],
            formDocument: e.options.formDocument,
          },
          id: null,
        }));

    fieldHelpers.setValue({
      ...field.value,
      serviceProvider: newValue,
      description: option.model.description,
      additionalFields,
    });
  };

  const handleTemplateFieldClear = () => {
    fieldHelpers.setValue({
      ...field.value,
      description: '',
      additionalFields: [],
    });
  };

  const renderAdditionalForm = (
    additionalFields: AdditionalFieldFormItemWithNullableIdModel[],
    serviceProviderId: string,
  ) => {
    return additionalFields.map((ad, i) => (
      <Col key={i} {...INTAKE_FORM_FIELDS_GRID_SIZES}>
        <FormField
          label={
            <>
              <FieldIssue
                fieldId={`serviceProviders["${serviceProviderId}"].additionalFields["${ad.id}"].valueJSON`}
              >
                {ad.name}
              </FieldIssue>
              {ad.description ? (
                <StyledFieldDescriptionText>
                  {ad.description}
                </StyledFieldDescriptionText>
              ) : null}
            </>
          }
          name={`${fieldName}.additionalFields.${i}.value`}
          component={AdditionalField}
          additionalProps={{
            renderPlaceholder: (validationRules: FieldValidationRule[]) =>
              generateFieldPlaceholderForAdditionalField(
                validationRules || [],
                ad,
                t,
              ),
            field: {
              type: ad.type,
              options: ad.options.selectOptions,
              formDocument: ad.options.formDocument,
              documentAssociation,
            },
          }}
        />
      </Col>
    ));
  };

  return (
    <StyledCard>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant="h5"
        title={t('intake_form.service_providers.service_provider_title', {
          index: index + 1,
        })}
      />
      <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
        <Tooltip
          placement="bottom"
          title={
            isMandatoryProvider
              ? t(
                  'intake_form.service_providers.mandatory_service_provider_tooltip',
                )
              : undefined
          }
        >
          <div>
            <FormField<ServiceProvidersAutocompleteProps>
              label={
                <FieldIssue
                  fieldId={`serviceProviders["${field.value.id}"].serviceProviderId`}
                >
                  {t('intake_form.service_providers.service_provider')}
                </FieldIssue>
              }
              disabled={isMandatoryProvider}
              name={`${fieldName}.serviceProvider`}
              component={ServiceProvidersAutocomplete}
              additionalProps={{
                initialValue: field.value.serviceProviderInitialValue,
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForSelect(validationRules, true, false, t),
                onSelect: handleTemplateSelect,
                onClear: handleTemplateFieldClear,
              }}
            />
          </div>
        </Tooltip>
        {field.value.description ? (
          <StyledDescriptionText>
            {field.value.description}
          </StyledDescriptionText>
        ) : null}
      </Col>
      {field.value && field.value.additionalFields.length
        ? renderAdditionalForm(field.value.additionalFields, field.value.id)
        : null}
      {deleteCallback ? (
        <HideIfDisabledForm hideIfSubmitting>
          <Button size="large" onClick={deleteCallback}>
            {t('remove', { ns: 'common' })}
          </Button>
        </HideIfDisabledForm>
      ) : null}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  .ant-card-body {
    min-height: 0px;
    padding: ${({ theme }) => theme.paddingXl};
  }
`;

const StyledDescriptionText = styled(Text)`
  margin-bottom: ${({ theme }) => theme.marginMd};
`;

const StyledFieldDescriptionText = styled(Text)`
  color: ${({ theme }) => theme.colorLightD1};
`;

export default ServiceProviderForm;
